import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Store
import { useAppDispatch } from 'store';
import { deleteDynamicSegmentFilter } from 'store/slices/dynamicSegment';
// Import UI components
import { Icon } from '@geneui/components';
// Import Components
import FilterDependencyDots, { DependencyPill, FilterInfoPopover } from 'components/common/FilterDependencyDots';
import FilterGroupItem from './FilterGroupItem';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
// Import Container
import { UIHashComparisonContainer } from '../UIHashContainers';
// Import Constants
import { l, ModalsClassNames, ActionsIcons } from 'constants/common';
// Import Helpers
import { hexToRgb } from 'services/Helpers';
// Import Enums
import { SegmentationCategoryTypeEnum } from 'enums';
// Import Styles
import styles from './FilterGroup.module.scss';

const FilterGroup = (props) => {
    const {
        groupDisplayName,
        list,
        color,
        uiType,
        resetActiveList,
        setEditingFilter,
        editingFilter,
        handleChangeActiveList,
        withoutActions,
        setReadOnly,
        readOnly,
        tabActiveIndex,
        schemaDeps,
        description,
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

    const setDeleteModalOpen = () => {
        setIsOpenDeleteModal(!isOpenDeleteModal);
    };

    const handleDeleteGroupFilters = () => {
        dispatch(
            deleteDynamicSegmentFilter({
                filterListHashKey: list[0]?.filter.parentHashKey,
                filterOrderIds: list.map((item) => item.orderId),
                tabId: tabActiveIndex,
            }),
        );
        setDeleteModalOpen();
    };

    const toFilterListCategory = () => {
        handleChangeActiveList(list[0]?.filter.parentHashKey);
    };

    const rgbColor = hexToRgb(color);

    return (
        <div
            className={classNames('crm-dynamic-segment-filters-item-wrapper', {
                'ai-predicted': uiType === SegmentationCategoryTypeEnum.AIPredicted,
            })}
        >
            <div
                className="crm-dynamic-segment-filters-item"
                style={
                    {
                        '--kpi-group-rgb-color': `rgb(${rgbColor})`,
                        '--kpi-group-rgb-color-disabled': `rgb(${rgbColor} / 40%)`,
                    } as React.CSSProperties
                }
            >
                <ConfirmationModal
                    isVisibleModal={isOpenDeleteModal}
                    onOk={handleDeleteGroupFilters}
                    onCancel={setDeleteModalOpen}
                    questionLabel={t(l.DoYouWantToDeleteThisFilterGroupFromDynamicSegmentFilterContainer)}
                    titleText={t(l.DeleteFilterGroupFromDynamicSegmentFilterContainer)}
                    iconType={ActionsIcons.Delete}
                    actionLabel={t(l.Delete)}
                    className={ModalsClassNames.Delete}
                    withPortal={true}
                />
                <div className="crm-dynamic-segment-filters-item__header crm-flex crm-justify-content-between crm-align-items-center">
                    <div
                        className={classNames(
                            'crm-dynamic-segment-filters-item__header__title-wrapper',
                            styles.filterGroupTitleWrapper,
                        )}
                    >
                        <div className="crm-dynamic-segment-filters-item__header__title">{groupDisplayName}</div>
                        {(schemaDeps.length > 0 || !isEmpty(description)) && (
                            <DependencyPill>
                                {schemaDeps.length > 0 && <FilterDependencyDots schemaDeps={schemaDeps} />}
                                {!isEmpty(description) && (
                                    <FilterInfoPopover title={groupDisplayName} description={description} />
                                )}
                            </DependencyPill>
                        )}
                    </div>
                    <div
                        className={`crm-dynamic-segment-filters-item__header__icons crm-flex crm-justify-content-between ${groupDisplayName
                            .replace(/\s+/g, '-')
                            .toLowerCase()}`}
                    >
                        {!withoutActions && (
                            <div className="crm-dynamic-segment-filters-item__header__icon-wrapper">
                                <Icon type="bc-icon-add" onClick={toFilterListCategory} disabled={readOnly} />
                            </div>
                        )}
                        {!withoutActions && (
                            <div className="crm-dynamic-segment-filters-item__header__icon-wrapper">
                                <Icon type="bc-icon-trash" onClick={setDeleteModalOpen} disabled={readOnly} />
                            </div>
                        )}
                    </div>
                </div>
                {list.map(({ filter, orderId }, index) => {
                    return (
                        <FilterGroupItem
                            resetActiveList={resetActiveList}
                            key={index}
                            item={filter}
                            setEditingFilter={setEditingFilter}
                            editingFilter={editingFilter}
                            handleChangeActiveList={handleChangeActiveList}
                            index={orderId}
                            withoutActions={withoutActions}
                            setReadOnly={setReadOnly}
                            readOnly={readOnly}
                            additionalData={filter?.additionalData}
                            tabActiveIndex={tabActiveIndex}
                        />
                    );
                })}
            </div>
        </div>
    );
};

FilterGroup.propTypes = {
    groupDisplayName: PropTypes.string,
    list: PropTypes.arrayOf(PropTypes.instanceOf(UIHashComparisonContainer)).isRequired,
    color: PropTypes.string,
    uiType: PropTypes.number,
    handleChangeActiveList: PropTypes.func,
    withoutActions: PropTypes.bool,
    readOnly: PropTypes.bool,
    editingFilter: PropTypes.object,
    setReadOnly: PropTypes.func,
    resetActiveList: PropTypes.func,
    setEditingFilter: PropTypes.func,
    tabActiveIndex: PropTypes.number.isRequired,
    schemaDeps: PropTypes.array,
    description: PropTypes.string,
};

FilterGroup.defaultProps = {
    groupDisplayName: '',
    withoutActions: false,
    editingFilter: null,
    resetActiveList: noop,
    setEditingFilter: noop,
    readOnly: false,
    setReadOnly: noop,
    handleChangeActiveList: noop,
    schemaDeps: [],
    description: '',
};

export default memo(FilterGroup);
